"use client";

import type React from "react";
import {
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  VStack,
  type ButtonProps,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import { smsAgentService } from "../../sms-services/agentService";
import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";
import SMSModelSelect from "./SMSModelSelect";
import SMSLanguageSelect from "./SMSLanguageSelect";

interface CreateAgentProps {
  onAgentCreated?: () => void;
  buttonProps?: Partial<ButtonProps>;
}

export const CreateAgent: React.FC<CreateAgentProps> = ({
  onAgentCreated,
  buttonProps,
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentClientId, clients } = useAuth();
  const credits = useStableCredits();
  const [agentName, setAgentName] = useState("");
  const [selectedModel, setSelectedModel] = useState("gpt-4o-mini");
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const [selectedTemperature, setSelectedTemperature] = useState(0.7);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateAgent = async () => {
    if (!agentName.trim()) {
      toast({
        title: "Agent name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsCreating(true);
    try {
      const selectedClient = clients.find((c) => c.id === currentClientId);
      const companyName = selectedClient?.company_name;

      await smsAgentService.create({
        name: agentName,
        model: selectedModel,
        language: selectedLanguage,
        temperature: selectedTemperature,
        ...(companyName && { company_name: companyName }),
      });

      toast({
        title: "SMS Agent created successfully",
        status: "success",
        duration: 3000,
      });

      // Reset form and close modal
      setAgentName("");
      setSelectedModel("gpt-4o-mini");
      setSelectedLanguage("en-US");
      setSelectedTemperature(0.7);
      onClose();

      // Call the callback if provided
      if (onAgentCreated) {
        onAgentCreated();
      }
    } catch (error: any) {
      console.error("Error creating SMS agent:", error);
      toast({
        title: "Error creating SMS agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the SMS agent",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Button
        onClick={onOpen}
        fontWeight="medium"
        leftIcon={<FontAwesomeIcon icon={faRobot} />}
        {...buttonProps}
        isDisabled={credits !== null && credits < 0.01}
        title={
          credits !== null && credits < 0.01
            ? "Insufficient balance to create agent"
            : undefined
        }
      >
        Create Agent
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader color="black">Create New SMS Agent</ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody pb={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel color="black">Agent Name</FormLabel>
                <Input
                  placeholder="Enter SMS agent name"
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  color="black"
                  _placeholder={{ color: "gray.400" }}
                  autoFocus
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="black">Model</FormLabel>
                <SMSModelSelect
                  value={selectedModel}
                  onChange={setSelectedModel}
                  onTemperatureChange={setSelectedTemperature}
                  initialTemperature={selectedTemperature}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel color="black">Language</FormLabel>
                <SMSLanguageSelect
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button
              onClick={onClose}
              mr={3}
              variant="ghost"
              color="black"
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAgent}
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              isLoading={isCreating}
              loadingText="Creating..."
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to create agent"
                  : undefined
              }
            >
              Create Agent
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
